// Build Channel Handler - Manages build_feature channel WebSocket communication
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DockerBuildContract } from '../types';
import {
    ChatMessage,
    ChatPage,
    initializeChatHistory,
    getChatMessages,
    addChatMessage,
    addChatMessages,
    setChatMessages,
    subscribeToChatMessages
} from '../../chat/components/sharedChatService';
import { getRouter } from '../../../../../shared/lib/comm/gateway_ws/connection';
// Note: All message parsing is handled by useVishCoderChat in shared chat folder
import { Channel } from '../../../../../shared/lib/comm/gateway_ws/outbound';
import { useWebSocketStatus } from '../../../../../shared/lib/comm/gateway_ws/useWebSocketStatus';
import BuildBottomPanel from './BuildBottomPanel';
import ContractPopupModal from './ContractPopupModal';
import { useBottomPanelInset } from './bottom-panel-inset-context';
import { ProgressStatusMessage } from './BuildProgressTab';
import { inferToolUsageKindFromContent } from './ai-activity-utils';
import { 
    addProgressUpdate, 
    getLatestBuildProgressUpdates, 
    getLatestBuild,
    getInProgressFeatures,
    hasAnyInProgressBuild,
    debugBuildTracking,
    getAiActivitySummaryForLatestBuild,
    updateBuildStatus,
    type AiActivitySummary
} from '../../../../../shared/lib/buildStatusTracker';
import { 
    addLogEntries,
    clearLogsStorage 
} from './logsStorageManager';
import { TokenManager } from '../../../../../shared/lib/apiClient';

/** Epic progress/finish: backend uses snake_case hlr_uiid / llr_uiid (legacy *_requirement_id still supported). */
function resolveEpicHlrLlrFromSources(
    bodyPayload: Record<string, unknown> | undefined,
    data: Record<string, unknown> | undefined,
    extra?: Record<string, unknown> | undefined
): { hlrUiid?: string; llrUiid?: string } {
    const bp = bodyPayload || {};
    const d = data || {};
    const x = extra || {};
    const hlrRaw =
        bp.hlr_uiid ??
        d.hlr_uiid ??
        x.hlr_uiid ??
        bp.high_level_requirement_id ??
        d.high_level_requirement_id ??
        x.high_level_requirement_id;
    const llrRaw =
        bp.llr_uiid ??
        d.llr_uiid ??
        x.llr_uiid ??
        bp.low_level_requirement_id ??
        d.low_level_requirement_id ??
        x.low_level_requirement_id;
    const hlrUiid = typeof hlrRaw === 'string' && hlrRaw.trim() ? hlrRaw.trim() : undefined;
    const llrUiid = typeof llrRaw === 'string' && llrRaw.trim() ? llrRaw.trim() : undefined;
    return { hlrUiid, llrUiid };
}

// Re-export centralizedWebSocketManager and hooks from shared location
export { centralizedWebSocketManager, useWebSocketConnection, useWebSocketMessages } from '../../../../../shared/lib/comm/gateway_ws/centralizedWebSocketManager';

// Types for payloads - these may need to be defined based on your protocol
type ContractPayload = {
    contract: {
        metadata?: Record<string, any>;
        settings?: Record<string, any>;
        requirements?: Record<string, any>;
    };
};

export interface BuildChannelMethods {
    addContractMessage: (contract: DockerBuildContract) => void;
    openContractModal: (contract: DockerBuildContract) => void;
    addUserMessage: (content: string) => void;
    addAssistantMessage: (content: string) => void;
    setChatMessages: (messages: ChatMessage[]) => void;
    setContractBuilding: (building: boolean) => void;
    getAgentSocket: () => WebSocket | null;
    getAgentConnected: () => boolean;
    sendContractToVishCoder: (contract: DockerBuildContract) => void;
    setContractPopups: React.Dispatch<React.SetStateAction<Set<string>>>;
}

interface BuildChannelHandlerProps {
    isBottomPanelOpen: boolean;
    onToggleBottomPanel: () => void;
    buildSummaryData: any;
    setBuildSummaryData: (data: any) => void;
    setIsBuildSummaryModalOpen: (open: boolean) => void;
    onInit?: (methods: BuildChannelMethods) => void;
    projectId?: number | string | null; // Project ID for organizing S3 uploads
}

// ChatMessage is now imported from sharedChatService

function sortProgressMessagesStable(messages: ProgressStatusMessage[]): ProgressStatusMessage[] {
    const copy = [...messages];
    copy.sort((a, b) => {
        if (!a.backendTs && !b.backendTs) return 0;
        if (!a.backendTs) return 1;
        if (!b.backendTs) return -1;
        return a.backendTs.localeCompare(b.backendTs);
    });
    return copy;
}

/** Union by backendId (fallback id); in-memory rows win on conflict — avoids capping UI at stored AI limit. */
function mergeProgressMessagesByKey(
    prev: ProgressStatusMessage[],
    loaded: ProgressStatusMessage[]
): ProgressStatusMessage[] {
    const map = new Map<string, ProgressStatusMessage>();
    for (const m of prev) {
        map.set(m.backendId || m.id, m);
    }
    for (const m of loaded) {
        const key = m.backendId || m.id;
        if (!map.has(key)) {
            map.set(key, m);
        }
    }
    return Array.from(map.values());
}

const BuildChannelHandler: React.FC<BuildChannelHandlerProps> = ({
    isBottomPanelOpen,
    onToggleBottomPanel,
    buildSummaryData,
    setBuildSummaryData,
    projectId,
    setIsBuildSummaryModalOpen,
    onInit,
}) => {
    const normalizeProjectId = useCallback((value: unknown): string | null => {
        if (value === null || value === undefined) return null;
        const normalized = String(value).trim();
        return normalized.length > 0 ? normalized : null;
    }, []);

    const extractPayloadProjectId = useCallback((payload: any): string | null => {
        const bodyPayload = payload?.body?.payload;
        const candidates = [
            payload?.project_id,
            payload?.projectId,
            payload?.body?.project_id,
            payload?.body?.projectId,
            bodyPayload?.project_id,
            bodyPayload?.projectId,
            bodyPayload?.statusDetails?.project_id,
            bodyPayload?.statusDetails?.projectId,
            bodyPayload?.contract?.metadata?.project_id,
            bodyPayload?.contract?.metadata?.projectId
        ];

        for (const candidate of candidates) {
            const normalized = normalizeProjectId(candidate);
            if (normalized) return normalized;
        }
        return null;
    }, [normalizeProjectId]);

    // Use shared WebSocket connection status hook
    const wsStatus = useWebSocketStatus();
    
    // Docker Logs WebSocket State
    const [logSocket, setLogSocket] = useState<WebSocket | null>(null);
    const [dockerLogs, setDockerLogs] = useState<string>('Connecting to log stream...');
    const [processedLogs, setProcessedLogs] = useState<string>('');
    const [activeBottomTab, setActiveBottomTab] = useState<'progress' | 'logs'>('progress');
    
    const { setInsetPx } = useBottomPanelInset();

    // Bottom panel resize state
    const [bottomPanelHeight, setBottomPanelHeight] = useState<number>(384); // Default: max-h-96 (384px)
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const bottomPanelHeightRef = useRef(bottomPanelHeight);
    const resizeStartY = useRef<number>(0);
    const resizeStartHeight = useRef<number>(384);
    /** True only between pointerdown on the handle and successful end of that gesture */
    const bottomPanelResizeGestureActiveRef = useRef(false);
    /** Detach listeners if component unmounts mid-drag */
    const detachBottomPanelResizeRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        bottomPanelHeightRef.current = bottomPanelHeight;
    }, [bottomPanelHeight]);

    // AI Agent WebSocket State (for contract sending and status updates)
    // Note: Connection state is managed by health check system via useWebSocketStatus hook
    const agentSocketRef = useRef<WebSocket | null>(null);
    const [agentStatus, setAgentStatus] = useState<string>('Connecting...');
    const [agentLogs, setAgentLogs] = useState<string>('');
    
    // Update agent status based on shared connection state from health check
    useEffect(() => {
        if (wsStatus.isConnected) {
            setAgentStatus('Connected and Idle');
            // Get socket from router when needed (health check manages the connection)
            const router = getRouter();
            const conn = router.getConnection();
            const socket = conn?.websocket || null;
            agentSocketRef.current = socket;
        } else if (wsStatus.isDisconnected) {
            setAgentStatus('Disconnected');
        }
    }, [wsStatus]);

    // Chat State - using shared chat service (for contract popups and status updates)
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [contractBuilding, setContractBuilding] = useState<boolean>(false);
    const [contractPopups, setContractPopups] = useState<Set<string>>(new Set());

    // Progress tab status messages - separate from chat service (directly from build_feature channel)
    const [progressStatusMessages, setProgressStatusMessages] = useState<ProgressStatusMessage[]>([]);
    /** Cumulative AI counts for current build (persists when only 20 AI lines are in localStorage). */
    const [aiActivitySummary, setAiActivitySummary] = useState<AiActivitySummary | null>(null);
    const epicHierarchyRef = useRef<Record<string, string[]>>({});

    const handleEpicBuildTrackingInitialized = useCallback((tracking: { hlrUiid: string; llrUiids: string[] }) => {
        const uniqueChildLlrs = Array.from(new Set(tracking.llrUiids.filter(Boolean)));
        epicHierarchyRef.current[tracking.hlrUiid] = uniqueChildLlrs;
    }, []);

    // Progress tab scroll reference
    const progressTabEndRef = useRef<HTMLDivElement | null>(null);

    // Track current active build (uiid and build_initiated_at) for saving progress updates
    const currentBuildRef = useRef<{ uiid: string; buildInitiatedAt: string } | null>(null);

    // Terminal logs for debugging
    const addTerminalLog = useCallback((message: string) => {
        const timestamp = new Date().toLocaleTimeString();
        console.log(`[${timestamp}] ${message}`);
    }, []);

    // Helper function to add progress status message with duplicate backendId checking
    const addProgressStatusMessage = useCallback((message: ProgressStatusMessage) => {
        setProgressStatusMessages(prev => {
            // Check if message with same backendId already exists
            if (message.backendId) {
                const isDuplicate = prev.some(m => m.backendId === message.backendId);
                if (isDuplicate) {
                    console.log(`⏭️ Skipping duplicate progress message with backendId: ${message.backendId}`);
                    return prev; // Return unchanged state
                }
            }
            // Add new message
            return [...prev, message];
        });
    }, []);

    // Connection status - health check system manages connection timing
    const getConnectionStatus = useCallback(() => {
        // Use shared WebSocket status based on health channel ping/pong
        if (wsStatus.isConnected) {
            return { text: "Connected", failed: false, canSend: true };
        }
        // Disconnected
        return { text: "Disconnected", failed: false, canSend: false };
    }, [wsStatus]);

    const canSendToAgent = useCallback(() => {
        return wsStatus.isConnected;
    }, [wsStatus]);

    // Initialize and subscribe to shared chat service (for contract popups only)
    useEffect(() => {
        // Initialize chat history from shared service
        initializeChatHistory();
        setChatMessages(getChatMessages('requirements'));
        
        // Subscribe to chat message changes (filtered by requirements page)
        const unsubscribe = subscribeToChatMessages((messages) => {
            setChatMessages([...messages]);
        }, 'requirements');
        
        return unsubscribe;
    }, []);

    // Load progress messages from localStorage on mount and when build status changes
    useEffect(() => {
        // Clear progress messages if no projectId
        if (!projectId) {
            setProgressStatusMessages([]);
            currentBuildRef.current = null;
            return;
        }
        
        const applyLoaded = (allProgressMessages: ProgressStatusMessage[], mergeWithExisting: boolean) => {
            if (!mergeWithExisting) {
                setProgressStatusMessages(sortProgressMessagesStable(allProgressMessages));
                return;
            }
            setProgressStatusMessages(prev => {
                if (allProgressMessages.length === 0) {
                    const hasInProgress =
                        getInProgressFeatures(projectId).length > 0;
                    if (!hasInProgress) {
                        return [];
                    }
                    return sortProgressMessagesStable(prev);
                }
                return sortProgressMessagesStable(mergeProgressMessagesByKey(prev, allProgressMessages));
            });
        };

        const loadProgressMessages = (mergeWithExisting: boolean) => {
            // Debug: Log what we're looking for
            console.log(`🔍 [BuildChannelHandler] Loading progress messages for projectId: ${projectId} (type: ${typeof projectId})`);
            debugBuildTracking();
            
            // Get all features with "In Progress" builds for this project
            let inProgressFeatures = getInProgressFeatures(projectId);
            console.log(`🔍 [BuildChannelHandler] Found ${inProgressFeatures.length} in-progress features for projectId: ${projectId}`);

            if (inProgressFeatures.length === 0) {
                currentBuildRef.current = null;
                applyLoaded([], mergeWithExisting);
                return;
            }
            
            // Load progress messages from the latest build of each in-progress feature
            const allProgressMessages: ProgressStatusMessage[] = [];
            
            for (const uiid of inProgressFeatures) {
                const latestBuild = getLatestBuild(projectId, uiid);
                if (latestBuild && latestBuild.build_status === 'In Progress') {
                    const progressUpdates = getLatestBuildProgressUpdates(projectId, uiid);
                    // Convert ProgressUpdate to ProgressStatusMessage and ensure uiid is set
                    const messagesWithUiid = progressUpdates.map(update => ({
                        ...update,
                        uiid: update.uiid || uiid // Use stored uiid or fallback to feature uiid
                    }));
                    allProgressMessages.push(...messagesWithUiid);
                    
                    // Update current build ref if not set
                    if (!currentBuildRef.current || 
                        latestBuild.build_initiated_at > 
                        (getLatestBuild(projectId, currentBuildRef.current.uiid)?.build_initiated_at || '')) {
                        currentBuildRef.current = {
                            uiid: uiid,
                            buildInitiatedAt: latestBuild.build_initiated_at
                        };
                    }
                }
            }
            
            allProgressMessages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
            
            console.log(`🔍 [BuildChannelHandler] Loaded ${allProgressMessages.length} progress messages for projectId: ${projectId}`);
            applyLoaded(allProgressMessages, mergeWithExisting);
        };
        
        // Load on mount — replace from storage (full reset for this project view)
        loadProgressMessages(false);
        
        // Storage updates must merge with live WebSocket rows so we are not capped at MAX_STORED_AI_MESSAGES.
        const handleBuildStatusChange = () => {
            loadProgressMessages(true);
        };
        
        window.addEventListener('buildStatusChanged', handleBuildStatusChange);
        
        return () => {
            window.removeEventListener('buildStatusChanged', handleBuildStatusChange);
        };
    }, [projectId]);

    // Cumulative AI totals for header (independent of MAX_STORED_AI_MESSAGES trimmed rows).
    useEffect(() => {
        if (!projectId) {
            setAiActivitySummary(null);
            return;
        }
        const uiid = currentBuildRef.current?.uiid;
        if (!uiid) {
            setAiActivitySummary(null);
            return;
        }
        setAiActivitySummary(getAiActivitySummaryForLatestBuild(projectId, uiid));
    }, [projectId, progressStatusMessages]);

    // Check for build complete triggers in progressStatusMessages (from build_feature channel)
    useEffect(() => {
        progressStatusMessages.forEach((message) => {
            // Check for build complete indicators
            const isBuildComplete = message.agent === 'Manager' && (
                message.content.toLowerCase().includes('build complete') ||
                message.content.toLowerCase().includes('build is complete') ||
                message.content.toLowerCase().includes('build completed') ||
                message.content.toLowerCase().includes('build finished') ||
                message.content.toLowerCase().includes('build done')
            );
            
            const isProgressComplete = message.progress !== null && message.progress !== undefined && message.progress >= 100;
            
            if ((isBuildComplete || isProgressComplete) && 
                (!buildSummaryData?.prLink || buildSummaryData.status !== 'Merged')) {
                setTimeout(() => {
                    setIsBuildSummaryModalOpen(true);
                    addTerminalLog('📋 Automatically opening BUILD SUMMARY modal');
                }, 2000);
            }
        });
    }, [progressStatusMessages, buildSummaryData, setIsBuildSummaryModalOpen, addTerminalLog]);

    // Process and beautify Docker logs
    const processDockerLogs = useCallback((rawLogs: string) => {
        const lines = rawLogs.split('\n');
        const processedLines: string[] = [];
        const hide_logs = [
            'health',
            'Health Check:',
            ' GET /'
        ];
        
        for (const line of lines) {
            if (!line.trim()) continue;
            
            const shouldHide = hide_logs.some(hideText => line.includes(hideText));
            if (shouldHide) continue;
            
            if (line.includes(' - ERROR - ')) {
                const errorLine = line.replace(
                    /(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}),\d+ - ERROR - (.+)/,
                    '<div class="docker-log-line docker-log-error">❌ $2 <span class="docker-log-separator">|</span> <span class="docker-log-timestamp">$1</span></div>'
                );
                processedLines.push(errorLine);
                continue;
            }
            
            if (line.includes(' - WARNING - ')) {
                const warningLine = line.replace(
                    /(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}),\d+ - WARNING - (.+)/,
                    '<div class="docker-log-line docker-log-warning">⚠️ $2 <span class="docker-log-separator">|</span> <span class="docker-log-timestamp">$1</span></div>'
                );
                processedLines.push(warningLine);
                continue;
            }
            
            processedLines.push(`<div class="docker-log-line">${line}</div>`);
        }
        
        return processedLines.join('');
    }, []);

    // Limit logs to last N lines
    const limitLogsToLastNLines = useCallback((logs: string, maxLines: number = 200) => {
        const lines = logs.split('\n');
        if (lines.length <= maxLines) return logs;
        return lines.slice(-maxLines).join('\n');
    }, []);

    // Note: PR information handling should be done in useVishCoderChat
    // This component only displays status messages from sharedChatService

    // WebSocket connection for Docker logs - using router
    const connectToLogStream = useCallback(() => {
        const router = getRouter();
        // Ensure main connection is established
        router.connect();
        
        // Use shared connection status - update logs based on status
        if (wsStatus.isConnected) {
            setDockerLogs("Log stream connected. Waiting for logs...\n");
            addTerminalLog('Docker log stream connected');
        } else if (wsStatus.isDisconnected) {
            setDockerLogs(prev => {
                const updatedLogs = prev + "\n--- DISCONNECTED ---\nLog stream has been closed.\n";
                return limitLogsToLastNLines(updatedLogs, 200);
            });
            addTerminalLog('Docker log stream disconnected');
        }

        // Subscribe to logs messages
        router.setChannelHandler('logs', (payload) => {
            // Extract backend ID and timestamp from payload root level
            const backendId = payload.id || '';
            const backendTs = payload.ts || '';
            
            // Check if this is a log_batch payload
            if (payload.body?.kind === 'logs' && payload.body?.payload?.type === 'log_batch') {
                // Extract lines array from payload
                const lines = payload.body.payload.lines || [];
                
                if (Array.isArray(lines) && lines.length > 0) {
                    // Extract just the log lines (they should already be strings)
                    // Filter out empty lines and ensure we only have strings
                    const validLines = lines
                        .filter(line => typeof line === 'string' && line.trim().length > 0)
                        .map(line => line.trim()); // Remove any extra whitespace
                    
                    if (validLines.length > 0) {
                        // Join them with newlines - each line is already a formatted log string
                        const logLines = validLines.join('\n') + '\n';
                        
                        const processedNewLogs = processDockerLogs(logLines);
                        
                        // Save each log line to localStorage
                        const logEntries = validLines.map((line, index) => ({
                            id: backendId ? `${backendId}-${index}` : `log-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                            content: line,
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            backendId: backendId || undefined,
                            backendTs: backendTs || undefined
                        }));
                        
                        try {
                            addLogEntries(logEntries);
                        } catch (error) {
                            console.warn('Failed to save logs to localStorage:', error);
                        }
                        
                        setDockerLogs(prev => {
                            const combinedLogs = prev + processedNewLogs;
                            const limitedLogs = limitLogsToLastNLines(combinedLogs, 200);
                            const processedCombinedLogs = processDockerLogs(limitedLogs);
                            setProcessedLogs(processedCombinedLogs);
                            return limitedLogs;
                        });
                        
                        setTimeout(() => {
                            const terminal = document.getElementById('docker-terminal');
                            if (terminal && activeBottomTab === 'logs') {
                                // Only auto-scroll if user is at or near the top (within 50px threshold)
                                const isNearTop = terminal.scrollTop < 50;
                                if (isNearTop) {
                                    terminal.scrollTop = 0;
                                }
                            }
                        }, 100);
                    }
                }
            } else {
                // Fallback to old format for backward compatibility
                const data = payload.body?.payload || payload;
                const logData = data?.data || data?.body?.data || (typeof data === 'string' ? data : JSON.stringify(data));
                const processedNewLogs = processDockerLogs(logData);
                
                // Save log to localStorage
                const logEntry = {
                    id: backendId || `log-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                    content: logData,
                    timestamp: backendTs ? new Date(backendTs) : new Date(),
                    backendId: backendId || undefined,
                    backendTs: backendTs || undefined
                };
                
                try {
                    addLogEntries([logEntry]);
                } catch (error) {
                    console.warn('Failed to save log to localStorage:', error);
                }
                
                setDockerLogs(prev => {
                    const combinedLogs = prev + processedNewLogs;
                    const limitedLogs = limitLogsToLastNLines(combinedLogs, 200);
                    const processedCombinedLogs = processDockerLogs(limitedLogs);
                    setProcessedLogs(processedCombinedLogs);
                    return limitedLogs;
                });
                
                setTimeout(() => {
                    const terminal = document.getElementById('docker-terminal');
                    if (terminal && activeBottomTab === 'logs') {
                        // Only auto-scroll if user is at or near the top (within 50px threshold)
                        const isNearTop = terminal.scrollTop < 50;
                        if (isNearTop) {
                            terminal.scrollTop = 0;
                        }
                    }
                }, 100);
            }
        });

        // Update logSocket ref
        const logConn = router.getConnection();
        setLogSocket(logConn?.websocket || null);

        return () => {
            router.removeChannelHandler('logs');
        };
    }, [addTerminalLog, processDockerLogs, limitLogsToLastNLines, wsStatus]);

    // Disconnect from log stream - using centralized manager
    const disconnectFromLogStream = useCallback(() => {
        // Note: We don't disconnect the main socket, just stop listening to logs channel
        setLogSocket(null);
        addTerminalLog('Docker log stream disconnected manually');
    }, [addTerminalLog]);

    // Connection initialization - health check system manages connection state
    useEffect(() => {
        const router = getRouter();
        router.connect(); // Health check will manage the connection lifecycle
        
        // Get initial socket reference (will be null until connection opens)
        const conn = router.getConnection();
        const socket = conn?.websocket || null;
        agentSocketRef.current = socket;
    }, []);

    // Handle build_feature/build_epic channel messages - route directly to Progress tab (NOT chat service)
    useEffect(() => {
        const router = getRouter();

        const handleBuildPayload = (payload: any) => {
            try {
                const selectedProjectId = normalizeProjectId(projectId);
                const payloadProjectId = extractPayloadProjectId(payload);
                if (
                    selectedProjectId &&
                    payloadProjectId &&
                    payloadProjectId !== selectedProjectId
                ) {
                    return;
                }

                if (payload.type === 'response' && (payload.channel === 'build_feature' || payload.channel === 'build_epic')) {
                    console.log(`[BuildChannelHandler] Full ${payload.channel} response payload:`, payload);
                }

                // Extract backend ID and timestamp from payload root level
                const backendId = payload.id || '';
                const backendTs = payload.ts || '';
                
                // Debug: Log the full payload structure
                console.log('[BuildChannelHandler] Received build payload:', {
                    channel: payload.channel,
                    backendId: backendId,
                    backendTs: backendTs,
                    bodyKind: payload.body?.kind,
                    bodyPayload: payload.body?.payload                    
                });
                
                // Extract data from payload
                // The backend sends: body.payload = {"statusDetails": {...}} when kind == "status"
                // So we need to check payload.body.payload.statusDetails
                const bodyPayload = payload.body?.payload;
                const data = bodyPayload || payload.body || payload;
                
                console.log('[BuildChannelHandler] Extracted data:', data);
                console.log('[BuildChannelHandler] Has statusDetails?', !!data.statusDetails);
                
                // Handle sync_status response
                if (payload.body?.kind === 'sync_status') {
                    console.log('[BuildChannelHandler] Processing sync_status response:', payload.body.payload);
                    
                    // Extract missing_statuses from the correct location in the payload
                    const missingStatuses = payload.body?.payload?.missing_statuses || [];
                    console.log('[BuildChannelHandler] Missing statuses array:', missingStatuses);
                    
                    if (Array.isArray(missingStatuses) && missingStatuses.length > 0) {
                        // Convert missing statuses to ProgressStatusMessage format
                        const newStatusMessages: ProgressStatusMessage[] = missingStatuses.map((status: any) => {
                            // Extract fields from backend payload structure
                            const backendMessageId = status.id || '';
                            const backendMessageTs = status.ts || '';
                            const frontendId = backendMessageId || `sync-status-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
                            const timestamp = backendMessageTs ? new Date(backendMessageTs) : new Date();
                            const details = status.details || 'No details provided';
                            const agent = status.agent || 'Unknown Agent';
                            const llm = status.LLM || 'None';
                            const progress = status.progress || null;
                            // Determine message type based on status or kind field
                            const messageType = (status.status === 'error' || status.kind === 'error') ? 'error' : 'status';
                            
                            return {
                                id: frontendId,
                                type: messageType as 'status' | 'error',
                                content: details,
                                agent: agent,
                                llm: llm,
                                progress: progress,
                                timestamp: timestamp,
                                uiid: status.uiid || currentBuildRef.current?.uiid,
                                backendId: backendMessageId,
                                backendTs: backendMessageTs
                            };
                        });
                        
                        // Add missing statuses to progress messages (avoid duplicates by backendId)
                        setProgressStatusMessages(prev => {
                            // Check for duplicate backendIds, not just frontend ids
                            const existingBackendIds = new Set(
                                prev.filter(m => m.backendId).map(m => m.backendId)
                            );
                            const uniqueNewMessages = newStatusMessages.filter(m => {
                                if (m.backendId && existingBackendIds.has(m.backendId)) {
                                    console.log(`⏭️ Skipping duplicate synced message with backendId: ${m.backendId}`);
                                    return false;
                                }
                                return true;
                            });
                            
                            // Merge and sort by timestamp in descending order (newest first)
                            const merged = [...prev, ...uniqueNewMessages];
                            merged.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
                            
                            return merged;
                        });
                        
                        // Save to localStorage if we have current build ref
                        if (currentBuildRef.current && projectId) {
                            newStatusMessages.forEach(message => {
                                try {
                                    addProgressUpdate(
                                        projectId,
                                        currentBuildRef.current!.uiid,
                                        currentBuildRef.current!.buildInitiatedAt,
                                        message
                                    );
                                } catch (error) {
                                    console.warn('Failed to save synced progress update to localStorage:', error);
                                }
                            });
                        }
                        
                        addTerminalLog(`🔄 Synced ${newStatusMessages.length} missing status messages`);
                    } else {
                        addTerminalLog('🔄 Sync completed: No missing status messages');
                    }
                    
                    return; // Exit early, we've handled sync_status
                }
                
                // Handle error messages from build channels
                // These messages have body.kind === "error" and payload.body.status === "error"
                if (payload.body?.kind === 'error' || payload.status === 'error') {
                    console.log('[BuildChannelHandler] Processing error message:', payload.body);
                    
                    // Extract error details
                    const errorPayload = payload.body?.payload as Record<string, unknown> | undefined;
                    const errorDetails = (errorPayload?.details ??
                                        errorPayload?.error ??
                                        errorPayload?.message ??
                                        'Unknown error occurred') as string;

                    const epicIdsOnError = resolveEpicHlrLlrFromSources(bodyPayload, data as Record<string, unknown>, errorPayload);
                    if (
                        payload.channel === 'build_epic' &&
                        projectId &&
                        epicIdsOnError.hlrUiid &&
                        epicIdsOnError.llrUiid
                    ) {
                        const llrB = getLatestBuild(projectId, epicIdsOnError.llrUiid);
                        if (llrB) {
                            updateBuildStatus(
                                projectId,
                                epicIdsOnError.llrUiid,
                                llrB.build_initiated_at,
                                'Error'
                            );
                        }
                        const hlrB = getLatestBuild(projectId, epicIdsOnError.hlrUiid);
                        if (hlrB) {
                            updateBuildStatus(
                                projectId,
                                epicIdsOnError.hlrUiid,
                                hlrB.build_initiated_at,
                                'Error'
                            );
                        }
                    }

                    // Get UIID from current build ref or find it
                    let messageUiid: string | undefined = undefined;
                    if (currentBuildRef.current) {
                        messageUiid = currentBuildRef.current.uiid;
                    } else {
                        const contractData = (data as any)?.contract || (bodyPayload as any)?.contract;
                        messageUiid =
                            contractData?.requirements?.low_level_requirements?.uiid ||
                            epicIdsOnError.llrUiid;
                    }

                    // Create error message for Progress tab
                    const errorMessage: ProgressStatusMessage = {
                        id: backendId || `build-feature-error-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                        type: 'error',
                        content: errorDetails,
                        agent: 'System',
                        llm: 'None',
                        timestamp: backendTs ? new Date(backendTs) : new Date(),
                        uiid: messageUiid,
                        backendId: backendId,
                        backendTs: backendTs
                    };
                    
                    // Add directly to Progress tab state (with duplicate backendId checking)
                    addProgressStatusMessage(errorMessage);
                    
                    // Save to localStorage - try to use current build ref, or find latest "In Progress" build
                    let targetUiid: string | null = null;
                    let targetBuildInitiatedAt: string | null = null;
                    
                    if (currentBuildRef.current) {
                        targetUiid = currentBuildRef.current.uiid;
                        targetBuildInitiatedAt = currentBuildRef.current.buildInitiatedAt;
                    } else if (projectId) {
                        // Find the latest "In Progress" build
                        const inProgressFeatures = getInProgressFeatures(projectId);
                        if (inProgressFeatures.length > 0) {
                            let latestUiid: string | null = null;
                            let latestTimestamp = '';
                            
                            for (const uiid of inProgressFeatures) {
                                const latestBuild = getLatestBuild(projectId, uiid);
                                if (latestBuild && latestBuild.build_status === 'In Progress') {
                                    if (!latestTimestamp || latestBuild.build_initiated_at > latestTimestamp) {
                                        latestTimestamp = latestBuild.build_initiated_at;
                                        latestUiid = uiid;
                                    }
                                }
                            }
                            
                            if (latestUiid) {
                                const latestBuild = getLatestBuild(projectId, latestUiid);
                                if (latestBuild) {
                                    targetUiid = latestUiid;
                                    targetBuildInitiatedAt = latestBuild.build_initiated_at;
                                    // Update ref for future messages
                                    currentBuildRef.current = {
                                        uiid: targetUiid,
                                        buildInitiatedAt: targetBuildInitiatedAt
                                    };
                                }
                            }
                        }
                    }
                    
                    // Save to localStorage if we have a target build
                    if (targetUiid && targetBuildInitiatedAt && projectId) {
                        try {
                            addProgressUpdate(projectId, targetUiid, targetBuildInitiatedAt, errorMessage);
                        } catch (error) {
                            console.warn('Failed to save error message to localStorage:', error);
                        }
                    }
                    
                    addTerminalLog(`❌ Error from backend: ${errorDetails}`);
                }

                const categoryMapStreaming: Record<string, 'ai_thinking' | 'ai_tool_usage' | 'ai_text'> = {
                    thinking: 'ai_thinking',
                    tool_usage: 'ai_tool_usage',
                    text: 'ai_text'
                };

                // Handle body.kind === "messages": array of { type, text, attachments }
                // (differs from flat body.payload.type + message used for kind === "status")
                if (payload.body?.kind === 'messages' && Array.isArray(bodyPayload?.messages)) {
                    bodyPayload.messages.forEach((msg: Record<string, unknown>, idx: number) => {
                        const messageType = msg?.type;
                        const messageContent =
                            msg?.text ?? msg?.message ?? msg?.content;
                        if (
                            !messageType ||
                            messageContent === undefined ||
                            !['tool_usage', 'thinking', 'text'].includes(String(messageType))
                        ) {
                            return;
                        }
                        const contentString =
                            typeof messageContent === 'string'
                                ? messageContent
                                : JSON.stringify(messageContent);
                        let messageUiid: string | undefined = currentBuildRef.current?.uiid;
                        if (!messageUiid && (data as any)?.contract?.requirements?.low_level_requirements?.uiid) {
                            messageUiid = (data as any).contract.requirements.low_level_requirements.uiid;
                        }
                        const uniqueBackendId = backendId ? `${backendId}-msg-${idx}` : undefined;
                        const resolvedCategory =
                            categoryMapStreaming[String(messageType)] || 'ai_text';
                        const streamingMessage: ProgressStatusMessage = {
                            id:
                                uniqueBackendId ||
                                `build-feature-${String(messageType)}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                            type: 'status',
                            content: contentString,
                            agent: (msg?.agent as string) ?? (bodyPayload as any)?.agent ?? data.agent ?? 'Agent',
                            llm: (msg?.LLM as string) ?? (bodyPayload as any)?.LLM ?? data.LLM ?? 'None',
                            progress: (msg?.progress as number | null | undefined) ?? null,
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            uiid: messageUiid,
                            backendId: uniqueBackendId,
                            backendTs: backendTs,
                            category: resolvedCategory,
                            toolUsageKind:
                                resolvedCategory === 'ai_tool_usage'
                                    ? inferToolUsageKindFromContent(contentString)
                                    : undefined
                        };
                        addProgressStatusMessage(streamingMessage);
                        if (currentBuildRef.current && projectId) {
                            try {
                                addProgressUpdate(
                                    projectId,
                                    currentBuildRef.current.uiid,
                                    currentBuildRef.current.buildInitiatedAt,
                                    streamingMessage
                                );
                            } catch (e) {
                                console.warn('Failed to save messages-batch progress update to localStorage:', e);
                            }
                        }
                        addTerminalLog(
                            `📊 ${String(messageType)}: ${contentString.slice(0, 80)}${contentString.length > 80 ? '...' : ''}`
                        );
                    });
                }
                
                // Handle streaming agent messages: type (tool_usage | thinking | text) + message
                // These are received as body.kind === "status" with body.payload.type and body.payload.message
                const messageType = bodyPayload?.type || data.type;
                const messageContent = bodyPayload?.message ?? bodyPayload?.content ?? data.message ?? data.content;
                const isStreamingAgentMessage = messageType && messageContent !== undefined &&
                    ['tool_usage', 'thinking', 'text'].includes(String(messageType));

                if (isStreamingAgentMessage) {
                    const contentString = typeof messageContent === 'string'
                        ? messageContent
                        : JSON.stringify(messageContent);
                    let messageUiid: string | undefined = currentBuildRef.current?.uiid;
                    if (!messageUiid && (data as any)?.contract?.requirements?.low_level_requirements?.uiid) {
                        messageUiid = (data as any).contract.requirements.low_level_requirements.uiid;
                    }
                    const resolvedCategory =
                        categoryMapStreaming[String(messageType)] || 'ai_text';
                    const streamingMessage: ProgressStatusMessage = {
                        id: backendId || `build-feature-${messageType}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                        type: 'status',
                        content: contentString,
                        agent: bodyPayload?.agent ?? data.agent ?? 'Agent',
                        llm: bodyPayload?.LLM ?? data.LLM ?? 'None',
                        progress: bodyPayload?.progress ?? data.progress ?? null,
                        timestamp: backendTs ? new Date(backendTs) : new Date(),
                        uiid: messageUiid,
                        backendId: backendId,
                        backendTs: backendTs,
                        category: resolvedCategory,
                        toolUsageKind:
                            resolvedCategory === 'ai_tool_usage'
                                ? inferToolUsageKindFromContent(contentString)
                                : undefined
                    };
                    addProgressStatusMessage(streamingMessage);
                    if (currentBuildRef.current && projectId) {
                        try {
                            addProgressUpdate(
                                projectId,
                                currentBuildRef.current.uiid,
                                currentBuildRef.current.buildInitiatedAt,
                                streamingMessage
                            );
                        } catch (e) {
                            console.warn('Failed to save streaming progress update to localStorage:', e);
                        }
                    }
                    addTerminalLog(`📊 ${messageType}: ${contentString.slice(0, 80)}${contentString.length > 80 ? '...' : ''}`);
                }

                // Handle status updates from build channels
                // These messages contain statusDetails with agent, LLM, details, and progress
                // Backend wraps payload under statusDetails when kind == "status"
                const statusDetails = data.statusDetails ||
                                     (bodyPayload && (bodyPayload as any).statusDetails) ||
                                     (payload.body && (payload.body as any).statusDetails);

                if (statusDetails) {
                    console.log('[BuildChannelHandler] Processing statusDetails:', statusDetails);
                    const agent = statusDetails.agent || 'Unknown Agent';
                    const llm = statusDetails.LLM || 'Unknown LLM';
                    const details = statusDetails.details || 'No details provided';
                    const progress = statusDetails.progress || null;
                    
                    let detailsString: string;
                    if (typeof details === 'object' && details !== null) {
                        if (details.file_name && details.file_type) {
                            detailsString = `${details.file_type} file: ${details.file_name}`;
                            if (details.file_size) {
                                detailsString += ` (${details.file_size} bytes)`;
                            }
                        } else {
                            detailsString = JSON.stringify(details);
                        }
                    } else {
                        detailsString = String(details);
                    }
                    
                    // Skip heartbeat-related messages
                    if (detailsString.toLowerCase().includes('heartbeat') || 
                        detailsString.toLowerCase().includes('main websocket connection')) {
                        return;
                    }
                    
                    // Get UIID from current build ref or find it
                    let messageUiid: string | undefined = undefined;
                    if (currentBuildRef.current) {
                        messageUiid = currentBuildRef.current.uiid;
                    } else {
                        // Try to find from contract data if available
                        const contractData = (data as any)?.contract || (bodyPayload as any)?.contract;
                        messageUiid = contractData?.requirements?.low_level_requirements?.uiid;
                    }
                    
                    // Create status message for Progress tab (directly, not via chat service)
                    const statusUpdateMessage: ProgressStatusMessage = {
                        id: backendId || `build-feature-status-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                        type: 'status',
                        content: detailsString,
                        agent: agent,
                        llm: llm,
                        progress: progress,
                        timestamp: backendTs ? new Date(backendTs) : new Date(),
                        uiid: messageUiid,
                        backendId: backendId,
                        backendTs: backendTs
                    };
                    
                    // Add directly to Progress tab state (NOT chat service, with duplicate backendId checking)
                    // Insert in chronological order (append to end since messages arrive in order)
                    addProgressStatusMessage(statusUpdateMessage);
                    
                    // Save to localStorage - try to use current build ref, or find latest "In Progress" build
                    let targetUiid: string | null = null;
                    let targetBuildInitiatedAt: string | null = null;
                    
                    if (currentBuildRef.current) {
                        targetUiid = currentBuildRef.current.uiid;
                        targetBuildInitiatedAt = currentBuildRef.current.buildInitiatedAt;
                    } else if (projectId) {
                        // Find the latest "In Progress" build
                        const inProgressFeatures = getInProgressFeatures(projectId);
                        if (inProgressFeatures.length > 0) {
                            // Use the most recent one
                            let latestUiid: string | null = null;
                            let latestTimestamp = '';
                            
                            for (const uiid of inProgressFeatures) {
                                const latestBuild = getLatestBuild(projectId, uiid);
                                if (latestBuild && latestBuild.build_status === 'In Progress') {
                                    if (!latestTimestamp || latestBuild.build_initiated_at > latestTimestamp) {
                                        latestTimestamp = latestBuild.build_initiated_at;
                                        latestUiid = uiid;
                                    }
                                }
                            }
                            
                            if (latestUiid) {
                                const latestBuild = getLatestBuild(projectId, latestUiid);
                                if (latestBuild) {
                                    targetUiid = latestUiid;
                                    targetBuildInitiatedAt = latestBuild.build_initiated_at;
                                    // Update ref for future messages
                                    currentBuildRef.current = {
                                        uiid: targetUiid,
                                        buildInitiatedAt: targetBuildInitiatedAt
                                    };
                                }
                            }
                        }
                    }
                    
                    // Save to localStorage if we have a target build
                    if (targetUiid && targetBuildInitiatedAt && projectId) {
                        try {
                            addProgressUpdate(projectId, targetUiid, targetBuildInitiatedAt, statusUpdateMessage);
                        } catch (error) {
                            console.warn('Failed to save progress update to localStorage:', error);
                        }
                    }
                    
                    addTerminalLog(`📊 Status update from ${agent}: ${detailsString}`);
                }
                
                // Handle contract status updates (InProgress, Completed, Failed)
                // Check status in multiple locations: payload.status, body.payload.status, or data.status
                const status = (payload.status as any) || 
                              (bodyPayload?.status as any) || 
                              (data.status as any) ||
                              ((statusDetails as any)?.status);
                const normalizedStatus = String(status || '').toLowerCase();
                const normalizedStatusCompact = normalizedStatus.replace(/[\s_-]/g, '');
                
                if (status) {
                    if (normalizedStatusCompact === 'inprogress') {
                        // Get UIID from contract data if available
                        const contractData = (data as any)?.contract || (bodyPayload as any)?.contract || (payload as any)?.contract;
                        const uiidFromContract = contractData?.requirements?.low_level_requirements?.uiid;
                        
                        const contractReceivedMessage: ProgressStatusMessage = {
                            id: backendId || `build-feature-inprogress-${Date.now()}`,
                            type: 'status',
                            content: '📥 Build feature request received. Processing started...',
                            agent: 'Manager',
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            uiid: uiidFromContract || currentBuildRef.current?.uiid,
                            backendId: backendId,
                            backendTs: backendTs
                        };
                        addProgressStatusMessage(contractReceivedMessage);
                        
                        // Try to find or set current build ref
                        let targetUiid: string | null = null;
                        let targetBuildInitiatedAt: string | null = null;
                        
                        // Reuse contractData and uiidFromContract from above
                        if (uiidFromContract && projectId) {
                            // Use this uiid and find the latest build
                            const latestBuild = getLatestBuild(projectId, uiidFromContract);
                            if (latestBuild && uiidFromContract) {
                                targetUiid = uiidFromContract;
                                targetBuildInitiatedAt = latestBuild.build_initiated_at;
                                // Update ref for future messages
                                currentBuildRef.current = {
                                    uiid: uiidFromContract,
                                    buildInitiatedAt: targetBuildInitiatedAt
                                };
                            }
                        } else if (currentBuildRef.current) {
                            // Use existing ref
                            targetUiid = currentBuildRef.current.uiid;
                            targetBuildInitiatedAt = currentBuildRef.current.buildInitiatedAt;
                        } else if (projectId) {
                            // Find the latest "In Progress" build
                            const inProgressFeatures = getInProgressFeatures(projectId);
                            if (inProgressFeatures.length > 0) {
                                let latestUiid: string | null = null;
                                let latestTimestamp = '';
                                
                                for (const uiid of inProgressFeatures) {
                                    const latestBuild = getLatestBuild(projectId, uiid);
                                    if (latestBuild && latestBuild.build_status === 'In Progress') {
                                        if (!latestTimestamp || latestBuild.build_initiated_at > latestTimestamp) {
                                            latestTimestamp = latestBuild.build_initiated_at;
                                            latestUiid = uiid;
                                        }
                                    }
                                }
                                
                                if (latestUiid) {
                                    const latestBuild = getLatestBuild(projectId, latestUiid);
                                    if (latestBuild && latestUiid) {
                                        targetUiid = latestUiid;
                                        targetBuildInitiatedAt = latestBuild.build_initiated_at;
                                        // Update ref for future messages
                                        currentBuildRef.current = {
                                            uiid: latestUiid,
                                            buildInitiatedAt: targetBuildInitiatedAt
                                        };
                                    }
                                }
                            }
                        }
                        
                        // Save to localStorage if we have a target build
                        if (targetUiid && targetBuildInitiatedAt && projectId) {
                            try {
                                addProgressUpdate(projectId, targetUiid, targetBuildInitiatedAt, contractReceivedMessage);
                            } catch (error) {
                                console.warn('Failed to save progress update to localStorage:', error);
                            }
                        }
                    } else if (normalizedStatusCompact === 'completed') {
                        // build_epic: per-LLR step uses hlr_uiid + llr_uiid (see gateway contract).
                        const epicIds = resolveEpicHlrLlrFromSources(
                            bodyPayload as Record<string, unknown>,
                            data as Record<string, unknown>,
                            statusDetails as Record<string, unknown> | undefined
                        );
                        const isEpicCompletionPayload =
                            payload.channel === 'build_epic' &&
                            !!epicIds.hlrUiid &&
                            !!epicIds.llrUiid;

                        if (isEpicCompletionPayload && projectId) {
                            const completedHlrUiid = epicIds.hlrUiid!;
                            const completedLlrUiid = epicIds.llrUiid!;
                            const llrBuild = getLatestBuild(projectId, completedLlrUiid);
                            if (llrBuild) {
                                updateBuildStatus(
                                    projectId,
                                    completedLlrUiid,
                                    llrBuild.build_initiated_at,
                                    'Completed'
                                );
                            }

                            const trackedChildLlrs = epicHierarchyRef.current[completedHlrUiid] || [];
                            const allChildLlrsCompleted =
                                trackedChildLlrs.length > 0 &&
                                trackedChildLlrs.every((llrUiid) => getLatestBuild(projectId, llrUiid)?.build_status === 'Completed');

                            const hlrBuild = getLatestBuild(projectId, completedHlrUiid);
                            if (hlrBuild) {
                                updateBuildStatus(
                                    projectId,
                                    completedHlrUiid,
                                    hlrBuild.build_initiated_at,
                                    allChildLlrsCompleted ? 'Completed' : 'In Progress'
                                );
                            }

                            const epicSummary =
                                typeof bodyPayload?.message === 'string' ? bodyPayload.message : null;
                            const epicCompletionMessage: ProgressStatusMessage = {
                                id: backendId || `build-epic-completed-${Date.now()}`,
                                type: 'status',
                                content:
                                    epicSummary ||
                                    (allChildLlrsCompleted
                                        ? `✅ Build epic completed: HLR ${completedHlrUiid} and all child LLRs are complete`
                                        : `✅ LLR completed: ${completedLlrUiid} (HLR ${completedHlrUiid} still in progress)`),
                                agent: 'Manager',
                                timestamp: backendTs ? new Date(backendTs) : new Date(),
                                uiid: completedLlrUiid,
                                backendId: backendId,
                                backendTs: backendTs
                            };
                            addProgressStatusMessage(epicCompletionMessage);
                        }

                        if (!isEpicCompletionPayload) {
                            const contractCompletedMessage: ProgressStatusMessage = {
                                id: backendId || `build-feature-completed-${Date.now()}`,
                                type: 'status',
                                content: '✅ Build feature processing completed!',
                                agent: 'Manager',
                                timestamp: backendTs ? new Date(backendTs) : new Date(),
                                uiid: currentBuildRef.current?.uiid,
                                backendId: backendId,
                                backendTs: backendTs
                            };
                            addProgressStatusMessage(contractCompletedMessage);

                            if (currentBuildRef.current && projectId) {
                                try {
                                    addProgressUpdate(
                                        projectId,
                                        currentBuildRef.current.uiid,
                                        currentBuildRef.current.buildInitiatedAt,
                                        contractCompletedMessage
                                    );
                                } catch (error) {
                                    console.warn('Failed to save progress update to localStorage:', error);
                                }
                            }
                        }
                    } else if (normalizedStatusCompact === 'failed' || normalizedStatusCompact === 'error') {
                        const errorMsg = bodyPayload?.error ||
                                        bodyPayload?.message ||
                                        data.error ||
                                        data.message ||
                                        statusDetails?.error ||
                                        statusDetails?.message ||
                                        'Unknown error';

                        const epicIdsFail = resolveEpicHlrLlrFromSources(
                            bodyPayload as Record<string, unknown>,
                            data as Record<string, unknown>,
                            statusDetails as Record<string, unknown> | undefined
                        );
                        if (
                            payload.channel === 'build_epic' &&
                            projectId &&
                            epicIdsFail.hlrUiid &&
                            epicIdsFail.llrUiid
                        ) {
                            const llrB = getLatestBuild(projectId, epicIdsFail.llrUiid);
                            if (llrB) {
                                updateBuildStatus(projectId, epicIdsFail.llrUiid, llrB.build_initiated_at, 'Error');
                            }
                            const hlrB = getLatestBuild(projectId, epicIdsFail.hlrUiid);
                            if (hlrB) {
                                updateBuildStatus(projectId, epicIdsFail.hlrUiid, hlrB.build_initiated_at, 'Error');
                            }
                        }

                        const contractErrorMessage: ProgressStatusMessage = {
                            id: backendId || `build-feature-error-${Date.now()}`,
                            type: 'status',
                            content: `❌ Build processing failed: ${errorMsg}`,
                            agent: 'Manager',
                            timestamp: backendTs ? new Date(backendTs) : new Date(),
                            uiid:
                                epicIdsFail.llrUiid ||
                                currentBuildRef.current?.uiid,
                            backendId: backendId,
                            backendTs: backendTs
                        };
                        addProgressStatusMessage(contractErrorMessage);

                        const progressUiid = epicIdsFail.llrUiid || currentBuildRef.current?.uiid;
                        const progressInitiatedAt =
                            (progressUiid && projectId && getLatestBuild(projectId, progressUiid)?.build_initiated_at) ||
                            currentBuildRef.current?.buildInitiatedAt;
                        if (progressUiid && progressInitiatedAt && projectId) {
                            try {
                                addProgressUpdate(projectId, progressUiid, progressInitiatedAt, contractErrorMessage);
                            } catch (error) {
                                console.warn('Failed to save progress update to localStorage:', error);
                            }
                        }
                    }
                }

                // Log when we receive a build message that didn't match any handler (helps verify reception)
                const bodyKind = payload.body?.kind as string | undefined;
                const wasHandled =
                    bodyKind === 'sync_status' ||
                    bodyKind === 'error' ||
                    bodyKind === 'messages' ||
                    payload.status === 'error' ||
                    isStreamingAgentMessage ||
                    !!statusDetails ||
                    !!status;
                if (!wasHandled) {
                    console.log('[BuildChannelHandler] Unhandled build payload (received but no display path):', {
                        bodyKind,
                        hasStatusDetails: !!statusDetails,
                        payloadKeys: bodyPayload ? Object.keys(bodyPayload) : []
                    });
                }
            } catch (error) {
                console.error('[BuildChannelHandler] Error processing build message:', error);
                addTerminalLog(`❌ Error processing build message: ${error}`);
            }
        };

        // Register handlers for both feature and epic build channels.
        router.setChannelHandler('build_feature', handleBuildPayload);
        router.setChannelHandler('build_epic', handleBuildPayload);

        // Cleanup: remove handler on unmount
        return () => {
            router.removeChannelHandler('build_feature');
            router.removeChannelHandler('build_epic');
        };
    }, [addTerminalLog, projectId, normalizeProjectId, extractPayloadProjectId]);

    // Disconnect from agent - using centralized manager
    const disconnectFromAgent = useCallback(() => {
        // Note: We don't disconnect the main socket, just stop listening to build_feature channel
        // Health check system manages the connection - we just update local status
        agentSocketRef.current = null;
        setAgentStatus('Disconnected');
        addTerminalLog('AI agent disconnected manually');
    }, [addTerminalLog]);


    // Expose methods for parent component
    const addContractMessage = useCallback((contract: DockerBuildContract) => {
        const contractMessage: ChatMessage = {
            id: `contract-${Date.now()}`,
            type: 'assistant' as const,
            content: `📄 Contract: Generated Successfully`,
            timestamp: new Date(),
            isContract: true,
            contractData: contract,
            page: 'requirements'
        };
        addChatMessage(contractMessage);
    }, []);

    // Open contract modal directly without opening chat
    const openContractModal = useCallback((contract: DockerBuildContract) => {
        const contractMessage: ChatMessage = {
            id: `contract-${Date.now()}`,
            type: 'assistant' as const,
            content: `📄 Contract: Generated Successfully`,
            timestamp: new Date(),
            isContract: true,
            contractData: contract,
            page: 'requirements'
        };
        addChatMessage(contractMessage);
        // Open the contract popup directly
        setContractPopups(prev => new Set([contractMessage.id]));
    }, []);

    const addUserMessage = useCallback((content: string) => {
        const userMessage: ChatMessage = {
            id: `user-${Date.now()}`,
            type: 'user' as const,
            content: content,
            timestamp: new Date(),
            page: 'requirements'
        };
        addChatMessage(userMessage);
    }, []);

    const addAssistantMessage = useCallback((content: string) => {
        const assistantMessage: ChatMessage = {
            id: `assistant-${Date.now()}`,
            type: 'assistant' as const,
            content: content,
            timestamp: new Date(),
            isContract: false,
            page: 'requirements'
        };
        addChatMessage(assistantMessage);
    }, []);

    const setChatMessagesCallback = useCallback((messages: ChatMessage[]) => {
        setChatMessages(messages); // Update shared service (this will notify all subscribers)
    }, []);

    // Get agent socket from router (health check manages the connection)
    const getAgentSocket = useCallback(() => {
        const router = getRouter();
        const conn = router.getConnection();
        const socket = conn?.websocket || null;
        agentSocketRef.current = socket;
        return socket;
    }, []);
    const getAgentConnected = useCallback(() => wsStatus.isConnected, [wsStatus]);

    // Open contract in Review modal only; actual send happens only via "Send to VishCoder" in ContractPopupModal
    const sendContractToVishCoder = useCallback((contract: DockerBuildContract) => {
        openContractModal(contract);
    }, [openContractModal]);

    // Call onInit when component mounts or when methods change
    // Use useRef to track if we've already called onInit to prevent infinite loops
    const onInitCalledRef = useRef(false);
    useEffect(() => {
        if (onInit && !onInitCalledRef.current) {
            onInit({
                addContractMessage,
                openContractModal,
                addUserMessage,
                addAssistantMessage,
                setChatMessages: setChatMessagesCallback,
                setContractBuilding,
                getAgentSocket,
                getAgentConnected,
                sendContractToVishCoder,
                setContractPopups,
            });
            onInitCalledRef.current = true;
        }
    }, [onInit, addContractMessage, addUserMessage, addAssistantMessage, getAgentSocket, getAgentConnected, sendContractToVishCoder, openContractModal, setChatMessagesCallback, setContractBuilding, setContractPopups]);

    // Chat history is now managed by sharedChatService - no need for separate load/save

    // Handle Escape key for contract popups
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && contractPopups.size > 0) {
                setContractPopups(new Set());
            }
        };
        
        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [contractPopups.size]);

    // Scroll to top when Logs tab is first opened
    useEffect(() => {
        const terminalElement = document.getElementById('docker-terminal');
        if (terminalElement && activeBottomTab === 'logs') {
            setTimeout(() => {
                if (terminalElement) {
                    terminalElement.scrollTop = 0;
                }
            }, 50);
        }
    }, [activeBottomTab]);

    // Auto-scroll Docker logs to top only when user is already at top
    useEffect(() => {
        const terminalElement = document.getElementById('docker-terminal');
        if (terminalElement && activeBottomTab === 'logs') {
            setTimeout(() => {
                const terminal = document.getElementById('docker-terminal');
                if (terminal) {
                    // Only auto-scroll if user is at or near the top (within 50px threshold)
                    // This prevents annoying auto-scroll when user is reading older logs
                    const isNearTop = terminal.scrollTop < 50;
                    if (isNearTop) {
                        terminal.scrollTop = 0;
                    }
                }
            }, 100);
        }
    }, [dockerLogs, processedLogs]);

    // Periodically clean up logs
    useEffect(() => {
        if (dockerLogs && dockerLogs.split('\n').filter(line => line.trim()).length > 200) {
            const limitedLogs = limitLogsToLastNLines(dockerLogs, 200);
            setDockerLogs(limitedLogs);
            const processedLimitedLogs = processDockerLogs(limitedLogs);
            setProcessedLogs(processedLimitedLogs);
            addTerminalLog('Docker logs truncated to last 200 lines');
        }
    }, [dockerLogs, limitLogsToLastNLines, processDockerLogs, addTerminalLog]);

    // Connect to log stream when bottom panel opens
    useEffect(() => {
        if (isBottomPanelOpen && !logSocket) {
            connectToLogStream();
        } else if (!isBottomPanelOpen && logSocket) {
            disconnectFromLogStream();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBottomPanelOpen, logSocket]);

    // Connection is handled by the useEffect above - no need for separate connectToAgent

    // Don't auto-connect when contract modal opens - connect only when user clicks Send
    // This prevents infinite reconnection loops when the agent socket fails to connect


    // Scroll to top when Progress tab is first opened
    useEffect(() => {
        if (progressTabEndRef.current && activeBottomTab === 'progress') {
            setTimeout(() => {
                if (progressTabEndRef.current) {
                    progressTabEndRef.current.scrollTop = 0;
                }
            }, 50);
        }
    }, [activeBottomTab]);

    // Auto-scroll progress tab to top only when user is already at top
    useEffect(() => {
        if (progressTabEndRef.current && progressStatusMessages.length > 0 && activeBottomTab === 'progress') {
            setTimeout(() => {
                if (progressTabEndRef.current) {
                    // Only auto-scroll if user is at or near the top (within 50px threshold)
                    // This prevents annoying auto-scroll when user is reading older messages
                    const isNearTop = progressTabEndRef.current.scrollTop < 50;
                    if (isNearTop) {
                        progressTabEndRef.current.scrollTop = 0;
                    }
                }
            }, 100);
        }
    }, [progressStatusMessages]);

    // Note: Connection state is managed by health check system
    // Health check logs will show connection status changes
    // No need for separate logging here - use wsStatus from useWebSocketStatus hook

    // Cleanup on unmount
    // Note: Health check system manages connection lifecycle
    // We don't disconnect the router here as other components might be using it
    useEffect(() => {
        return () => {
            // Cleanup is handled by health check system
        };
    }, []);

    // Bottom panel resize: only pointerdown→release on the handle changes height. `setPointerCapture`
    // keeps the gesture when the cursor crosses a cross-origin iframe (Sandbox Preview).
    const handleResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.preventDefault();
        detachBottomPanelResizeRef.current?.();

        const el = e.currentTarget;
        const pointerId = e.pointerId;
        let captureOk = false;
        try {
            el.setPointerCapture(pointerId);
            captureOk = true;
        } catch {
            /* ignore */
        }

        bottomPanelResizeGestureActiveRef.current = true;
        resizeStartY.current = e.clientY;
        resizeStartHeight.current = bottomPanelHeightRef.current;
        setIsResizing(true);
        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';

        let cleaned = false;
        const cleanup = () => {
            if (cleaned) return;
            cleaned = true;
            bottomPanelResizeGestureActiveRef.current = false;
            setIsResizing(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            el.removeEventListener('pointermove', onPointerMove);
            el.removeEventListener('pointerup', onPointerUp);
            el.removeEventListener('pointercancel', onPointerCancel);
            el.removeEventListener('lostpointercapture', onLostCapture);
            if (!captureOk) {
                window.removeEventListener('pointermove', onWindowPointerMove);
                window.removeEventListener('pointerup', onWindowPointerUp);
                window.removeEventListener('pointercancel', onWindowPointerCancel);
            }
            window.removeEventListener('blur', onWindowBlur);
            try {
                if (el.hasPointerCapture(pointerId)) {
                    el.releasePointerCapture(pointerId);
                }
            } catch {
                /* already released */
            }
            detachBottomPanelResizeRef.current = null;
        };

        const applyMove = (clientY: number) => {
            if (!bottomPanelResizeGestureActiveRef.current) return;
            const deltaY = resizeStartY.current - clientY; // Negative when dragging up (increasing height)
            const newHeight = Math.max(200, Math.min(800, resizeStartHeight.current + deltaY));
            setBottomPanelHeight(newHeight);
        };

        const onPointerMove = (moveEvent: PointerEvent) => {
            if (moveEvent.pointerId !== pointerId) return;
            applyMove(moveEvent.clientY);
        };

        const onWindowPointerMove = (moveEvent: PointerEvent) => {
            if (moveEvent.pointerId !== pointerId) return;
            applyMove(moveEvent.clientY);
        };

        const onPointerUp = (upEvent: PointerEvent) => {
            if (upEvent.pointerId !== pointerId) return;
            cleanup();
        };

        const onWindowPointerUp = (upEvent: PointerEvent) => {
            if (upEvent.pointerId !== pointerId) return;
            cleanup();
        };

        const onPointerCancel = (cancelEvent: PointerEvent) => {
            if (cancelEvent.pointerId !== pointerId) return;
            cleanup();
        };

        const onWindowPointerCancel = (cancelEvent: PointerEvent) => {
            if (cancelEvent.pointerId !== pointerId) return;
            cleanup();
        };

        const onLostCapture = (lostEvent: PointerEvent) => {
            if (lostEvent.pointerId !== pointerId) return;
            cleanup();
        };

        const onWindowBlur = () => {
            cleanup();
        };

        el.addEventListener('pointermove', onPointerMove);
        el.addEventListener('pointerup', onPointerUp);
        el.addEventListener('pointercancel', onPointerCancel);
        el.addEventListener('lostpointercapture', onLostCapture);
        if (!captureOk) {
            window.addEventListener('pointermove', onWindowPointerMove);
            window.addEventListener('pointerup', onWindowPointerUp);
            window.addEventListener('pointercancel', onWindowPointerCancel);
        }
        window.addEventListener('blur', onWindowBlur);

        detachBottomPanelResizeRef.current = cleanup;
    }, []);

    useEffect(
        () => () => {
            detachBottomPanelResizeRef.current?.();
        },
        [],
    );

    // Expose methods and state to parent
    // Note: agentSocket is available via getAgentSocket() method
    // Connection state is managed by health check system via wsStatus
    const exposed = {
        chatMessages,
        setChatMessages,
        contractBuilding,
        setContractBuilding,
        contractPopups,
        setContractPopups,
        agentSocket: agentSocketRef.current, // Get from ref (managed by health check)
        agentConnected: wsStatus.isConnected, // From health check system
        agentStatus,
    };

    // Make exposed data available to parent via ref or context if needed
    // For now, we'll just render the panels

    const handleClearLogs = useCallback(() => {
        setDockerLogs('Connecting to log stream...');
        setProcessedLogs('');
        // Clear logs from localStorage
        clearLogsStorage();
        addTerminalLog('🗑️ Logs cleared from storage');
    }, [addTerminalLog]);

    const handleToggleConnection = useCallback(() => {
        if (wsStatus.isConnected) {
            disconnectFromLogStream();
        } else {
            connectToLogStream();
        }
    }, [wsStatus.isConnected, disconnectFromLogStream, connectToLogStream]);

    // Sync progress status messages handler
    const handleSyncProgress = useCallback(() => {
        const router = getRouter();
        const comm = router.getCommunicationManager();
        
        if (!comm || !wsStatus.isConnected) {
            addTerminalLog('❌ Cannot sync: WebSocket not connected');
            return;
        }
        
        // Use UIID from current build ref as thread_id (Low Level Requirement UIID)
        let threadId = currentBuildRef.current?.uiid || '';
        
        // If no current build ref, try to find the latest "In Progress" build
        if (!threadId && projectId) {
            const inProgressFeatures = getInProgressFeatures(projectId);
            if (inProgressFeatures.length > 0) {
                let latestUiid: string | null = null;
                let latestTimestamp = '';
                
                for (const uiid of inProgressFeatures) {
                    const latestBuild = getLatestBuild(projectId, uiid);
                    if (latestBuild && latestBuild.build_status === 'In Progress') {
                        if (!latestTimestamp || latestBuild.build_initiated_at > latestTimestamp) {
                            latestTimestamp = latestBuild.build_initiated_at;
                            latestUiid = uiid;
                        }
                    }
                }
                
                if (latestUiid) {
                    threadId = latestUiid;
                    // Update current build ref for future syncs
                    const latestBuild = getLatestBuild(projectId, latestUiid);
                    if (latestBuild) {
                        currentBuildRef.current = {
                            uiid: latestUiid,
                            buildInitiatedAt: latestBuild.build_initiated_at
                        };
                    }
                }
            }
        }
        
        // If still no threadId, log warning and return
        if (!threadId) {
            addTerminalLog('⚠️ Cannot sync: No active build UIID found');
            return;
        }
        
        // Get last received message ID (backend ID of the most recent message by backend timestamp)
        let lastReceivedMessageId = '';
        if (progressStatusMessages.length > 0) {
            // Filter messages that have backend ID and TS
            const messagesWithBackendData = progressStatusMessages.filter(m => m.backendId && m.backendTs);
            
            if (messagesWithBackendData.length > 0) {
                // Sort by backend timestamp (ISO string comparison works for ISO dates)
                const sortedMessages = [...messagesWithBackendData].sort((a, b) => {
                    const tsA = a.backendTs || '';
                    const tsB = b.backendTs || '';
                    return tsB.localeCompare(tsA); // Descending order (latest first)
                });
                
                // Use the backend ID from the latest message
                lastReceivedMessageId = sortedMessages[0].backendId || '';
                
                console.log('[BuildChannelHandler] Latest message for sync:', {
                    backendId: sortedMessages[0].backendId,
                    backendTs: sortedMessages[0].backendTs,
                    content: sortedMessages[0].content
                });
            }
        }
        
        // Prepare sync payload with UIID as thread_id
        const syncPayload = {
            thread_id: threadId,
            last_received_message_id: lastReceivedMessageId
        };
        console.log('[BuildChannelHandler] Full payload sent to backend (sync_status):', JSON.stringify(syncPayload, null, 2));
       
        // Set channel context so origin includes projectId, userId, featureId for backend
        const userId = TokenManager.getUserId();
        const messageId = crypto.randomUUID();
        const timestamp = new Date().toISOString();
        router.setChannelContext('build_feature', {
            threadId,
            originMessageId: messageId,
            originActor: 'user',
            origints: timestamp,
            ...(userId && { userId }),
            ...(projectId != null && { projectId: String(projectId) }),
            ...(threadId && { featureId: threadId })
        }, messageId, 'user', timestamp);
       
        // Send sync_status request
        const sent = comm.emit('build_feature', 'user', 'streaming', 'sync_status', syncPayload, 'request'); 
        if (sent) {
            addTerminalLog(`🔄 Sync request sent (UIID: ${threadId || 'N/A'}, last_message_id: ${lastReceivedMessageId || 'N/A'})`);
        } else {
            addTerminalLog('❌ Failed to send sync request');
        }
    }, [wsStatus.isConnected, progressStatusMessages, addTerminalLog, projectId]);

    // Automatic sync every 5 minutes (only when there are in-progress builds)
    useEffect(() => {
        // Only sync if connected and there are in-progress builds
        if (!wsStatus.isConnected) return;
        
        // Call sync immediately on mount (after a short delay) if there are in-progress builds
        const initialSyncTimer = setTimeout(() => {
            if (projectId && hasAnyInProgressBuild(projectId)) {
                handleSyncProgress();
            }
        }, 2000); // 2 second delay on mount
        
        // Set up interval for periodic sync every 5 minutes
        const syncInterval = setInterval(() => {
            if (wsStatus.isConnected && projectId && hasAnyInProgressBuild(projectId)) {
                handleSyncProgress();
            }
        }, 5 * 60 * 1000); // 5 minutes
        
        return () => {
            clearTimeout(initialSyncTimer);
            clearInterval(syncInterval);
        };
    }, [wsStatus.isConnected, handleSyncProgress, projectId]);

    // Shorten fixed chat panel so it sits above this terminal (see VishCoderChatPanel).
    useEffect(() => {
        setInsetPx(isBottomPanelOpen ? bottomPanelHeight : 0);
    }, [isBottomPanelOpen, bottomPanelHeight, setInsetPx]);

    useEffect(() => {
        return () => setInsetPx(0);
    }, [setInsetPx]);

    return (
        <>
            {/* Bottom Panel - Tabs (Progress & Docker Logs) */}
            <BuildBottomPanel
                isOpen={isBottomPanelOpen}
                height={bottomPanelHeight}
                isResizing={isResizing}
                activeTab={activeBottomTab}
                progressStatusMessages={progressStatusMessages}
                aiActivitySummary={aiActivitySummary}
                progressTabEndRef={progressTabEndRef}
                dockerLogs={dockerLogs}
                processedLogs={processedLogs}
                onToggle={onToggleBottomPanel}
                onTabChange={setActiveBottomTab}
                onResizeStart={handleResizeStart}
                onClearLogs={handleClearLogs}
                onToggleConnection={handleToggleConnection}
            />

            {/* Contract Popup Modal */}
            <ContractPopupModal
                contractPopups={contractPopups}
                setContractPopups={setContractPopups}
                addTerminalLog={addTerminalLog}
                getConnectionStatus={getConnectionStatus}
                canSendToAgent={canSendToAgent}
                onEpicBuildTrackingInitialized={handleEpicBuildTrackingInitialized}
            />
        </>
    );
};

export default BuildChannelHandler;
